import { API_CONFIG, logApiCall } from '../config/api';

export interface ChatHistoryMessage {
  role: string;
  content: string;
}

export interface ChatMessage {
  role: string;
  content: string;
}

export interface AgentChatRequest {
  session_id: string;
  history: ChatHistoryMessage[];
  current_message: ChatMessage;
  message: string;
  output_type: 'chat';
  input_type: 'chat';
}

export interface AgentChatResponse {
  response?: string;
  response_text?: string;
  content?: string;
  session_id: string;
  [key: string]: any;
}

// Get or create a unique session ID
export function getOrCreateSessionId(): string {
  const SESSION_KEY = 'agent_chat_session_id';
  let sessionId = localStorage.getItem(SESSION_KEY);
  
  if (!sessionId) {
    // Generate a unique session ID using timestamp and random string
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem(SESSION_KEY, sessionId);
  }
  
  return sessionId;
}

// Clear the session ID (useful for starting a new conversation)
export function clearSessionId(): void {
  const SESSION_KEY = 'agent_chat_session_id';
  localStorage.removeItem(SESSION_KEY);
}

// Get the agent chat API base URL
function getAgentChatBaseUrl(): string {
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';
  
  // Use environment variable if available, otherwise use localhost for development
  return process.env.REACT_APP_AGENT_CHAT_BASE_URL || 
    (env === 'local' ? 'http://localhost:8008' : 'http://localhost:8008');
}

/**
 * Send a message to the agent chat API
 * @param message - The user's message
 * @param userRole - The user's role (for better responses)
 * @param history - Optional chat history (currently left blank as per requirements)
 * @returns Promise with the agent's response
 */
export async function sendAgentChatMessage(
  message: string,
  userRole: string,
  history: ChatHistoryMessage[] = []
): Promise<AgentChatResponse> {
  const sessionId = getOrCreateSessionId();
  const baseUrl = getAgentChatBaseUrl();
  const endpoint = '/api/v1/agent/chat';
  const url = `${baseUrl}${endpoint}`;
  
  const requestBody: AgentChatRequest = {
    session_id: sessionId,
    history: history, // Left blank for now as per requirements
    current_message: {
      role: userRole,
      content: message,
    },
    message: message,
    output_type: 'chat',
    input_type: 'chat',
  };
  
  logApiCall('POST', url, requestBody);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Agent Chat] Error response:', errorText);
      throw new Error(`Agent chat API error: ${response.status} - ${errorText}`);
    }
    
    const data: AgentChatResponse = await response.json();
    console.log('[Agent Chat] Response:', data);
    
    return data;
  } catch (error: any) {
    console.error('[Agent Chat] Error:', error);
    throw new Error(`Failed to communicate with agent: ${error.message}`);
  }
}

/**
 * Check if the agent chat API is available
 */
export async function checkAgentChatAvailability(): Promise<boolean> {
  try {
    const baseUrl = getAgentChatBaseUrl();
    const response = await fetch(`${baseUrl}/health`, { method: 'GET' });
    return response.ok;
  } catch (error) {
    console.warn('[Agent Chat] Service not available:', error);
    return false;
  }
}

/**
 * Extract and clean HTML content from agent response
 * Handles markdown code fences and extracts body content from full HTML documents
 */
function cleanAgentHtml(rawHtml: string): string {
  let cleaned = rawHtml;
  
  // Remove markdown code fences (```html and ```)
  cleaned = cleaned.replace(/^```html\s*/i, '').replace(/\s*```$/, '');
  
  // Check if it's a full HTML document
  if (cleaned.includes('<!DOCTYPE') || cleaned.includes('<html')) {
    // Extract content between <body> tags
    const bodyMatch = cleaned.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch && bodyMatch[1]) {
      cleaned = bodyMatch[1].trim();
    }
    
    // If no body tags, try to extract content between <main> or other common containers
    if (!bodyMatch) {
      const mainMatch = cleaned.match(/<main[^>]*>([\s\S]*?)<\/main>/i);
      if (mainMatch && mainMatch[1]) {
        cleaned = mainMatch[1].trim();
      }
    }
  }
  
  // Remove any <style> tags that might interfere with our layout
  cleaned = cleaned.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  
  // Remove any inline style attributes that might set width
  cleaned = cleaned.replace(/\s*style\s*=\s*["'][^"']*["']/gi, '');
  
  console.log('[Agent HTML Cleaner] Original length:', rawHtml.length);
  console.log('[Agent HTML Cleaner] Cleaned length:', cleaned.length);
  console.log('[Agent HTML Cleaner] Cleaned preview:', cleaned.substring(0, 200));
  
  return cleaned;
}

/**
 * Send message to agent message endpoint for content display
 * @param message - The user's message
 * @param sessionId - Optional session ID (uses stored one if not provided)
 * @returns Promise with the agent's HTML response
 */
export async function sendAgentMessage(
  message: string,
  sessionId?: string
): Promise<{ response_text: string; session_id: string }> {
  const sid = sessionId || getOrCreateSessionId();
  const baseUrl = getAgentChatBaseUrl();
  const endpoint = '/api/v1/agent/message';
  const url = `${baseUrl}${endpoint}`;
  
  const requestBody = {
    session_id: sid,
    message: message,
  };
  
  logApiCall('POST', url, requestBody);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Agent Message] Error response:', errorText);
      throw new Error(`Agent message API error: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    console.log('[Agent Message] Raw response:', data);
    
    const rawHtml = data.response_text || data.response || data.content || '';
    const cleanedHtml = cleanAgentHtml(rawHtml);
    
    return {
      response_text: cleanedHtml,
      session_id: data.session_id || sid,
    };
  } catch (error: any) {
    console.error('[Agent Message] Error:', error);
    throw new Error(`Failed to get agent message response: ${error.message}`);
  }
}

